@use '../../../src/styles/Colors.scss' as Colors;
@use '../../../src/styles/Constants.scss' as Constants;

$var-minimum-cell-width: 100px;
$var-display-cell-width: 70px;
$var-resizer-width: 16px;
$var-gap-size: Constants.$val-spacing-l;
$var-shadow: 0 2px 2px 0 Colors.$col-shadow;
$var-company-header-height: 64px;
$var-tabs-navigation-height: 64px;
$var-breadcrumbs-header-height: 56px;
$var-table-header-height: 50px;
$var-table-action-header-height: 64px;
$var-table-footer-height: 64px;
$var-dropdown-z-index: 1000;

@mixin icon {
	color: Colors.$col-greyMid;
	margin-left: Constants.$val-spacing-xs;
}

@mixin cell ($type) {
	font-weight: normal;
	min-width: $var-minimum-cell-width;
	display: flex;
	align-items: center;
	height: 100%;
	white-space: nowrap;

	@if $type ==header {
		background-color: Colors.$col-greyBackground;
		font-size: Constants.$val-fontSizeSmall;
	}

	@if $type ==regular {
		padding: Constants.$val-spacing-s Constants.$val-spacing-s;
		background-color: inherit;
		text-overflow: ellipsis;
	}

	@if $type ==sub_row {
		padding: 0 Constants.$val-spacing-s Constants.$val-spacing-m Constants.$val-spacing-s;
		background-color: inherit;
	}

	&:has(div > :global(.show.dropdown)) {
		z-index: 2;
		background-color: transparent;
	}

	&:has(div > :global(.show.dropup)) {
		z-index: 2;
		background-color: transparent;
	}

	& :global(.tooltip__trigger-wrapper) {
		overflow: hidden;
		white-space: nowrap;
		text-overflow: ellipsis;
	}


}

@mixin row {
	display: grid;
	align-items: start;
	gap: $var-gap-size;
	padding: 0 Constants.$val-spacing-l;
	border-bottom: 1px solid Colors.$col-greyConnection;
}

@mixin text-with-sort {
	cursor: pointer;
	padding: Constants.$val-spacing-s Constants.$val-spacing-s;
	text-overflow: ellipsis;
	white-space: nowrap;
	overflow-x: hidden;
	display: flex;
}

@mixin resizer {
	background-color: Colors.$col-greyBackground;
	cursor: col-resize;
	min-width: $var-resizer-width;
	max-width: $var-resizer-width;
	// placing resizer to the right most part of column cell, due to existence of gap property
	margin-right: (-$var-gap-size);
	height: 100%;
}

@mixin table-grid ($type) {
	&__row {
		@include row;

		&__settings {
			display: flex;
			align-items: center;
			height: 100%;
			width: Constants.$val-spacing-m;

			[class^="icon-settings"] {
				cursor: pointer;
				color: Colors.$col-greyMid;
				font-size: Constants.$val-fontSizeSmall;

				&:hover {
					color: Colors.$col-black;
				}
			}
		}

		&__header-cell {
			@include cell($type);
			justify-content: space-between;

			&__text-with-sort {
				@include text-with-sort;

				&>[class^="icon-"] {
					@include icon;
				}
			}

			&__resizer {
				@include resizer;
			}

			&:last-child {

				// remove for last resizer so it doesn't push rows to the left
				&>span:last-child {
					margin-right: 0;
				}
			}
		}

		&__data-cell {
			@include cell($type);
		}

		&__sub-row-data-cell {
			@include cell(sub_row)
		}


		@if $type ==regular {
			cursor: pointer;
			background-color: inherit;
		}

		&--non-last {
			border-bottom: 0;
		}
	}

}

.container {
	display: grid;
	grid-template-columns: 1fr;
	max-width: 100%;

	&__table-container {
		display: grid;
		box-shadow: $var-shadow;
		border: 1px solid Colors.$col-greyConnection;
		background-color: Colors.$col-white;
		height: fit-content;

		&__table-header {
			padding: Constants.$val-spacing-m Constants.$val-spacing-l;
			background-color: Colors.$col-greyLight;
			border-bottom: 1px solid Colors.$col-greyConnection;
		}

		&__table {
			display: grid;
			border-bottom: 1px solid Colors.$col-greyConnection;
			overflow: auto;

			@mixin head {
				background-color: Colors.$col-greyBackground;
				position: sticky;
				top: 0;
				z-index: 2;
				@include table-grid(header);
			}

			&__head {
				@include head;
			}

			@mixin body {
				position: relative;
				height: calc(
					100vh 
					- $var-company-header-height 
					- $var-breadcrumbs-header-height 
					- $var-tabs-navigation-height 
					- $var-table-action-header-height 
					- $var-table-footer-height 
					- Constants.$val-spacing-xl
					- var(--extra-table-offset, 0px)
				);
			}

			&__body {
				@include body;

				&__fit-content {
					@include body;
					height: fit-content;
					max-height: 500px;
					overflow-y: auto;
				}

				&__row-group {
					background-color: Colors.$col-white;

					&:hover {
						background-color: Colors.$col-orangeHover;
					}

					&--active-in-side-panel {
						background-color: Colors.$col-design-blue50;

						&:hover {
							background-color: Colors.$col-design-blue50;
						}
					}
				}

				@mixin no-rows {
					display: inline-block;
					position: sticky;
					left: 50%;
					background-color: Colors.$col-white;
					padding: Constants.$val-spacing-m;
					top: 50%;
					text-align: center;
					transform: translateX(-50%);
					color: Colors.$col-design-grey10;
					font-style: italic;
					height: fit-content;
				}

				&__no-rows {
					@include no-rows;

					&__fit-content {
						@include no-rows;
						top: 0;
						transform: translate(0, 0);
					}
				}

				@include table-grid(regular);
			}
		}

		// if there is a table header, reduce table height by table header's height
		&:has(.container__table-container__table-header) {
			.container__table-container__table__body {
				height: calc(
					100vh 
					- $var-company-header-height 
					- $var-breadcrumbs-header-height 
					- $var-tabs-navigation-height 
					- $var-table-header-height 
					- $var-table-action-header-height 
					- $var-table-footer-height 
					- 4 * Constants.$val-spacing-xl
					- var(--extra-table-offset, 0px)
				);
			}
		}
	}

	&__side-panel {
		box-shadow: $var-shadow;
		border-top: 1px solid Colors.$col-greyConnection;
		border-bottom: 1px solid Colors.$col-greyConnection;
		border-right: 1px solid Colors.$col-greyConnection;
		background-color: Colors.$col-greyBackground;
		overflow-x: hidden;
		overflow-y: auto;

		&__header {
			height: calc($var-table-action-header-height + 1px); // plus border
			border-bottom: 1px solid Colors.$col-greyConnection;

			&:has(.container__side-panel__header__default) {
				display: flex;
				align-items: center;
				& > span {
					width: 100%;
					padding-left: Constants.$val-spacing-l;
				}
			}

			&__default {
				text-transform: uppercase;
				font-weight: bold;
			}
		}

		&__body {
			max-height: 100%;
			background-color: Colors.$col-greyBackground;

			height: calc(
					100vh 
					- $var-company-header-height 
					- $var-breadcrumbs-header-height 
					- $var-tabs-navigation-height 
					- $var-table-action-header-height
					- Constants.$val-spacing-xxl
					- var(--extra-table-offset, 0px)
				);

			&__hint {
				padding: Constants.$val-spacing-l;
				color: Colors.$col-greyInactive;
			}
		}

		&__body-with-footer {
			overflow: auto;
			max-height: 100%;
			height: calc(
					100vh
					- $var-company-header-height
					- $var-breadcrumbs-header-height
					- $var-tabs-navigation-height
					- $var-table-action-header-height
					- $var-table-footer-height
					- Constants.$val-spacing-xl
					- var(--extra-table-offset, 2px)
				);

			&__hint {
				padding: Constants.$val-spacing-l;
				color: Colors.$col-greyInactive;
			}
		}

		&__footer {
			height: calc($var-table-footer-height + 2px); // plus border
			border-top: 1px solid Colors.$col-greyConnection;

			&:has(.container__side-panel__footer__default) {
				display: flex;
				align-items: center;
				& > span {
					width: 100%;
					padding-left: Constants.$val-spacing-l;
				}
			}

			&__default {
				text-transform: uppercase;
				font-weight: bold;
			}
		}
	}

	// if there is a table header, reduce table height by table header's height
	&:has(.table-container__table-header) {
		.table-container__table__body {
			height: calc(
				100vh 
				- $var-company-header-height 
				- $var-breadcrumbs-header-height 
				- $var-tabs-navigation-height 
				- $var-table-header-height 
				- $var-table-action-header-height 
				- $var-table-footer-height 
				- 4 * Constants.$val-spacing-xl
				- var(--extra-table-offset, 0px)
			);
		}
	}
}

.fixed {
	position: sticky;
}

.last-fixed {
	border-right: 1px solid Colors.$col-greyConnection;

	// not moving resizer because we want visible right border
	&>span:last-child {
		margin-right: 0;
	}
}

.display-column {
	min-width: $var-display-cell-width;
}

.right-aligned-column-header {
	justify-content: flex-end;
}

.loading {
	display: flex;
	align-items: center;
	justify-content: center;
	width: 100%;
	height: 100%;
}

.spinner-icon {
	font-size: Constants.$val-fontSizeXXBig;
	margin-right: Constants.$val-spacing-s;
	animation: spin 1s linear infinite;
}

@keyframes spin {
	to {
		transform: rotate(360deg);
	}
}

$var-checkbox-size: 16px;

.select-checkbox {
	position: relative;
  
	input[type='checkbox'] {
		-webkit-appearance: none;
		-moz-appearance: none;
		appearance: none;
		
		width: $var-checkbox-size;
		height: $var-checkbox-size;
		border: 2px solid Colors.$col-greyMid;
		background-color: transparent;
		border-radius: 2px;
		cursor: pointer;

		&:focus:not(:checked)
		{
			border-color: Colors.$col-greyMid !important;
			background-color: transparent;
		}

		&:checked {
			background-color: Colors.$col-orange;
			border-color: Colors.$col-orange;
			
			&::after {
				content: '';
				position: absolute;
				left: 5px;
				top: 0px;
				width: 6px;
				height: 12px;

				border-right: 2px solid #fff;
				border-bottom: 2px solid #fff;
				transform: rotate(45deg);
			}
		}
	}
}
